// src/components/work/AddWorkModal.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  Building2,
  CalendarDays,
  MapPin,
  Users,
  User as UserIcon,
  Info,
  Search,
  X,
  Plus,
  Clock,
  Tag as TagIcon,
  FileText,
} from "lucide-react";

import api from "../../api/axios";
import { useCreateWork } from "../../hooks/useWorkQueries";
import {
  useUsers,
  useUserSearch,
  useUsersByIds,
} from "../../hooks/useUserQueries";

/* =============================================================================
   THEME PRIMITIVES (mirrors AddClientModal)
============================================================================= */
const INPUT_BASE =
  "block w-full rounded-lg border border-gray-300 bg-white px-3.5 py-2.5 text-[0.95rem] leading-6 text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500";
const SELECT_BASE = INPUT_BASE + " pr-9";
const LABEL_BASE = "block text-[13px] font-medium text-gray-700";
const HINT_BASE = "text-[12px] text-gray-500";

function Section({ icon: Icon, title, hint, children }) {
  return (
    <div className="border rounded-xl p-5 space-y-4 shadow-sm bg-white">
      <div className="flex items-start gap-2">
        <div className="mt-0.5 text-gray-700">
          <Icon size={18} />
        </div>
        <div>
          <div className="text-gray-800 font-semibold">{title}</div>
          {hint ? <div className={HINT_BASE}>{hint}</div> : null}
        </div>
      </div>
      {children}
    </div>
  );
}

function LabeledInput({
  id,
  name,
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  required = false,
  disabled = false,
  autoComplete = "off",
  rightAdornment = null,
  onKeyDown,
  onFocus,
}) {
  const hasRight = !!rightAdornment;
  return (
    <div className="space-y-1.5">
      {label ? (
        <label htmlFor={id || name} className={LABEL_BASE}>
          {label} {required ? <span className="text-red-500">*</span> : null}
        </label>
      ) : null}
      <div className="relative">
        <input
          id={id || name}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          type={type}
          disabled={disabled}
          autoComplete={autoComplete}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          className={[INPUT_BASE, hasRight ? "pr-10" : ""].join(" ")}
          required={required}
        />
        {hasRight ? (
          <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-gray-400">
            {rightAdornment}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function LabeledSelect({
  id,
  name,
  label,
  value,
  onChange,
  children,
  required = false,
  disabled = false,
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id || name} className={LABEL_BASE}>
        {label} {required ? <span className="text-red-500">*</span> : null}
      </label>
      <select
        id={id || name}
        name={name}
        value={value}
        onChange={onChange}
        className={SELECT_BASE}
        disabled={disabled}
        required={required}
      >
        {children}
      </select>
    </div>
  );
}

/* =============================================================================
   UTILS
============================================================================= */
const TYPES = ["task", "job", "appointment"];
const PRIORITIES = ["low", "normal", "high", "urgent"];

const DEFAULTS = {
  task: { priority: "normal" },
  job: { priority: "normal" },
  appointment: { priority: "normal" },
};

function deriveTypeFromPath(pathname) {
  if (pathname.startsWith("/work/jobs")) return "job";
  if (pathname.startsWith("/work/schedule")) return "appointment";
  return "task";
}

function toISO(dtLocal) {
  if (!dtLocal) return undefined;
  try {
    const d = new Date(dtLocal);
    if (!Number.isFinite(d.getTime())) return undefined;
    return d.toISOString();
  } catch {
    return undefined;
  }
}

function initials(name = "") {
  const parts = String(name).trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? "").join("");
}

function cx(...xs) {
  return xs.filter(Boolean).join(" ");
}

function useDebouncedValue(value, delay = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

const parseList = (payload) =>
  Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload?.items)
    ? payload.items
    : Array.isArray(payload)
    ? payload
    : [];

/* ── Local relevance ranking for clients (prefix > word-start > contains) */
function scoreClientMatch(q, c) {
  const query = (q || "").trim().toLowerCase();
  if (!query) return 0;
  const name = (c?.name || "").toLowerCase();
  const email = (c?.email || "").toLowerCase();
  const company = (c?.company || c?.companyName || "").toLowerCase();

  const wordStart = (s) => {
    const i = s.indexOf(query);
    if (i < 0) return false;
    return i === 0 || /\s|_|-|\.|,/.test(s[i - 1]);
  };

  let s = 0;
  if (name.startsWith(query)) s += 120;
  else if (wordStart(name)) s += 100;
  else {
    const i = name.indexOf(query);
    if (i >= 0) s += 80 - Math.min(i, 60);
  }

  if (email.startsWith(query)) s += 60;
  else {
    const i = email.indexOf(query);
    if (i >= 0) s += 40 - Math.min(i, 30);
  }

  if (company.startsWith(query)) s += 35;
  else {
    const i = company.indexOf(query);
    if (i >= 0) s += 20 - Math.min(i, 20);
  }
  return s;
}

function sortClientsByRelevance(list = [], query = "") {
  const q = (query || "").trim().toLowerCase();
  if (!q) {
    return [...list].sort((a, b) =>
      (a?.name || "").localeCompare(b?.name || "", undefined, {
        sensitivity: "base",
      })
    );
  }
  return [...list]
    .map((c, i) => ({ c, i, s: scoreClientMatch(q, c) }))
    .sort((A, B) => {
      if (B.s !== A.s) return B.s - A.s;
      const an = (A.c?.name || "").toLowerCase();
      const bn = (B.c?.name || "").toLowerCase();
      const t = an.localeCompare(bn);
      return t !== 0 ? t : A.i - B.i;
    })
    .map((x) => x.c);
}

/* =============================================================================
   CLIENT COMBOBOX — clearable & editable even after selection
============================================================================= */
function ClientCombobox({
  selectedClientId,
  onSelectClientId,
  onSelectClientObject, // for address sync
  disabled,
}) {
  const [clientQuery, setClientQuery] = useState("");
  const [clientOpen, setClientOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);

  const [selectedClient, setSelectedClient] = useState(null);

  const debounced = useDebouncedValue(clientQuery, 250);
  const rootRef = useRef(null);

  useEffect(() => {
    function handleOutside(e) {
      const el = rootRef.current;
      if (!el) return;
      if (!el.contains(e.target)) setClientOpen(false);
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  const { data, isFetching, isError } = useQuery({
    queryKey: ["clients", debounced],
    queryFn: async () => {
      const params = debounced ? { search: debounced } : {};
      const res = await api.get("/clients", { params });
      return parseList(res.data);
    },
    enabled: clientOpen,
    staleTime: 60_000,
  });

  // Ranked/filtered list (works even if backend returns unsorted results)
  const displayClients = useMemo(() => {
    const base = Array.isArray(data) ? data : [];
    const q = clientQuery.trim().toLowerCase();
    if (!q) return sortClientsByRelevance(base, "");
    const filtered = base.filter((c) => {
      const n = (c?.name || "").toLowerCase();
      const e = (c?.email || "").toLowerCase();
      const co = (c?.company || c?.companyName || "").toLowerCase();
      return n.includes(q) || e.includes(q) || co.includes(q);
    });
    return sortClientsByRelevance(filtered, q);
  }, [data, clientQuery]);

  // 🔁 Hydrate when parent sets selectedClientId (e.g., Quote selection)
  useEffect(() => {
    async function hydrateSelected() {
      if (!selectedClientId) {
        if (selectedClient) setSelectedClient(null);
        if (clientQuery) setClientQuery("");
        return;
      }
      const already =
        selectedClient &&
        String(selectedClient._id || selectedClient.id) ===
          String(selectedClientId);
      if (already) return;

      try {
        const res = await api.get(`/clients/${selectedClientId}`);
        const c =
          Array.isArray(res.data) ? res.data[0] : res?.data?.data || res?.data || null;
        if (c) {
          setSelectedClient(c);
          setClientQuery(c.name || c.email || String(selectedClientId));
          onSelectClientObject?.(c);
        } else {
          setClientQuery(String(selectedClientId));
        }
      } catch {
        setClientQuery(String(selectedClientId));
      }
    }

    hydrateSelected();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId]);

  // ⛔️ Removed auto-select (exact/only match). Require explicit selection.

  // If user edits the input after a selection, clear the selection
  useEffect(() => {
    const display =
      selectedClient?.name || selectedClient?.email || selectedClientId || "";
    if (!display) return;
    if (clientQuery && clientQuery !== display) {
      if (selectedClientId) onSelectClientId("");
      if (selectedClient) setSelectedClient(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientQuery]);

  return (
    <div ref={rootRef} className="relative">
      <div className="space-y-1.5">
        <label className={LABEL_BASE}>Client</label>
        <div
          className={cx(
            "flex items-center gap-2 rounded-lg border px-3.5 py-2.5",
            disabled ? "bg-gray-50" : "bg-white"
          )}
        >
          <Search className="text-gray-400" size={16} />
          <input
            value={clientQuery}
            onChange={(e) => {
              const v = e.target.value;
              setClientQuery(v);
              setClientOpen(true);
              setHighlightIdx(0);
              // clear any prior selection when typing (explicit selection required)
              if (selectedClient) setSelectedClient(null);
              if (selectedClientId) onSelectClientId("");
              onSelectClientObject?.(null);
            }}
            onFocus={() => setClientOpen(true)}
            placeholder="Search client by name or email"
            disabled={disabled}
            className="w-full border-0 p-0 text-[0.95rem] outline-none placeholder:text-gray-400"
          />
          {(selectedClientId || clientQuery) && !disabled ? (
            <button
              type="button"
              aria-label="Clear client"
              className="rounded-full p-1 text-gray-600 hover:bg-gray-100"
              onClick={() => {
                setClientQuery("");
                setClientOpen(false);
                onSelectClientId("");
                setSelectedClient(null);
                onSelectClientObject?.(null);
              }}
            >
              <X size={16} />
            </button>
          ) : null}
        </div>

        {selectedClient ? (
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium border border-blue-100">
              {selectedClient.name || selectedClient.email || "Selected client"}
              <button
                type="button"
                className="rounded-full px-1 text-blue-600 hover:bg-blue-100"
                onClick={() => {
                  onSelectClientId("");
                  setSelectedClient(null);
                  onSelectClientObject?.(null);
                }}
                aria-label="Remove selected client"
                title="Remove"
              >
                ✕
              </button>
            </span>
          </div>
        ) : null}
      </div>

      {clientOpen && (
        <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-xl border bg-white shadow-xl">
          {isError ? (
            <div className="px-3 py-3 text-sm text-red-600">Failed to load clients.</div>
          ) : isFetching ? (
            <div className="px-3 py-3 text-sm text-gray-500">Loading…</div>
          ) : displayClients.length === 0 ? (
            <div className="px-3 py-3 text-sm text-gray-500">No results</div>
          ) : (
            <ul className="max-h-64 overflow-auto py-1">
              {displayClients.map((c, idx) => {
                const id = String(c._id || c.id || "");
                const active = idx === highlightIdx;
                return (
                  <li key={id}>
                    <button
                      type="button"
                      className={cx(
                        "flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50",
                        active ? "bg-blue-50" : ""
                      )}
                      onMouseEnter={() => setHighlightIdx(idx)}
                      onClick={() => {
                        onSelectClientId(id);
                        setSelectedClient(c);
                        onSelectClientObject?.(c);
                        setClientOpen(false);
                        setClientQuery(c.name || c.email || id);
                      }}
                    >
                      <div className="grid h-7 w-7 place-items-center rounded-full bg-gray-100 text-[10px] font-semibold text-gray-700">
                        {initials(c.name || c.email || "?")}
                      </div>
                      <div className="min-w-0">
                        <div className="truncate text-gray-900">
                          {c.name || "(No name)"}
                        </div>
                        {c.email ? (
                          <div className="truncate text-xs text-gray-500">
                            {c.email}
                          </div>
                        ) : null}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/* =============================================================================
   QUOTE COMBOBOX — for Jobs (optional link)
============================================================================= */
function QuoteCombobox({
  selectedQuoteId,
  onSelectQuoteId,
  onSelectQuoteObject, // to auto-set client/address
  disabled,
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const debounced = useDebouncedValue(q, 250);
  const rootRef = useRef(null);

  useEffect(() => {
    function handleOutside(e) {
      const el = rootRef.current;
      if (!el) return;
      if (!el.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  const { data, isFetching, isError } = useQuery({
    queryKey: ["quotes", "for-combobox", debounced],
    queryFn: async () => {
      const params = debounced
        ? {
            search: debounced,
            include: ["client"],
            populate: ["client"],
            expand: ["client"],
          }
        : { include: ["client"], populate: ["client"], expand: ["client"] };
      const res = await api.get("/quotes", { params });
      return parseList(res.data);
    },
    enabled: open,
    staleTime: 60_000,
  });

  const list = Array.isArray(data) ? data : [];

  return (
    <div ref={rootRef} className="relative">
      <div className="space-y-1.5">
        <label className={LABEL_BASE}>Link Quote (optional)</label>
        <div
          className={cx(
            "flex items-center gap-2 rounded-lg border px-3.5 py-2.5",
            disabled ? "bg-gray-50" : "bg-white"
          )}
        >
          <Search className="text-gray-400" size={16} />
          <input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setOpen(true);
              setHighlightIdx(0);
            }}
            onFocus={() => setOpen(true)}
            placeholder="Search by quote number / client"
            disabled={disabled}
            className="w-full border-0 p-0 text-[0.95rem] outline-none placeholder:text-gray-400"
          />
          {(selectedQuoteId || q) && !disabled ? (
            <button
              type="button"
              aria-label="Clear quote"
              className="rounded-full p-1 text-gray-600 hover:bg-gray-100"
              onClick={() => {
                setQ("");
                setOpen(false);
                onSelectQuoteId("");
                onSelectQuoteObject?.(null);
              }}
            >
              <X size={16} />
            </button>
          ) : null}
        </div>

        {selectedQuoteId ? (
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1 bg-violet-50 text-violet-700 px-2 py-0.5 rounded-full text-xs font-medium border border-violet-100">
              Quote #{selectedQuoteId.slice(-6)}
              <button
                type="button"
                className="rounded-full px-1 text-violet-700 hover:bg-violet-100"
                onClick={() => {
                  onSelectQuoteId("");
                  onSelectQuoteObject?.(null);
                }}
                aria-label="Remove linked quote"
                title="Remove"
              >
                ✕
              </button>
            </span>
          </div>
        ) : null}
      </div>

      {open && (
        <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-xl border bg-white shadow-xl">
          {isError ? (
            <div className="px-3 py-3 text-sm text-red-600">Failed to load quotes.</div>
          ) : isFetching ? (
            <div className="px-3 py-3 text-sm text-gray-500">Loading…</div>
          ) : list.length === 0 ? (
            <div className="px-3 py-3 text-sm text-gray-500">No results</div>
          ) : (
            <ul className="max-h-64 overflow-auto py-1">
              {list.map((qObj, idx) => {
                const id = String(qObj._id || qObj.id || qObj.quoteId || "");
                const num = qObj.quoteNumber || qObj.number || id.slice(-6);
                const clientName =
                  qObj.client?.name || qObj.clientName || qObj.client?.email || "";
                const active = idx === highlightIdx;

                return (
                  <li key={id}>
                    <button
                      type="button"
                      className={cx(
                        "flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-slate-50",
                        active ? "bg-violet-50" : ""
                      )}
                      onMouseEnter={() => setHighlightIdx(idx)}
                      onClick={async () => {
                        // hydrate client if quote only contains a client id
                        let qAug = qObj;
                        const rawClientId =
                          qObj.clientId ||
                          (typeof qObj.client === "string"
                            ? qObj.client
                            : qObj.client?._id || qObj.client?.id);

                        if (
                          (!qObj.client || typeof qObj.client === "string") &&
                          rawClientId
                        ) {
                          try {
                            const cRes = await api.get(`/clients/${rawClientId}`);
                            const client = cRes?.data?.data || cRes?.data || null;
                            if (client) qAug = { ...qObj, clientId: rawClientId, client };
                            else qAug = { ...qObj, clientId: rawClientId };
                          } catch {
                            qAug = { ...qObj, clientId: rawClientId };
                          }
                        }

                        onSelectQuoteId(id);
                        onSelectQuoteObject?.(qAug);
                        setOpen(false);
                        const displayClient =
                          qAug.client?.name ||
                          qAug.clientName ||
                          qAug.client?.email ||
                          "";
                        setQ(`Q-${num}${displayClient ? ` • ${displayClient}` : ""}`);
                      }}
                    >
                      <FileText size={16} className="text-violet-600" />
                      <div className="min-w-0">
                        <div className="truncate text-gray-900">Quote #{num}</div>
                        {clientName ? (
                          <div className="truncate text-xs text-gray-500">
                            {clientName}
                          </div>
                        ) : null}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/* =============================================================================
   USERS MULTI-SELECT (chips + dropdown) — uses hooks for search + hydrate
============================================================================= */
function UsersMultiSelect({ value = [], onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const debounced = useDebouncedValue(q, 200);
  const rootRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleOutside(e) {
      const el = rootRef.current;
      if (!el) return;
      if (!el.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  // Selected -> hydrate objects for chip labels/avatars
  const { data: hydrated = [] } = useUsersByIds(value, {
    enabled: value.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // Fallback team list when search is empty
  const { data: team = [] } = useUsers({
    enabled: open && debounced.trim().length < 2,
    limit: 25,
  });

  // Typeahead results when term length >= 2
  const { data: searchResults = [], isFetching, isError } = useUserSearch(
    debounced,
    { enabled: open, limit: 12 }
  );

  const showingSearch = debounced.trim().length >= 2;
  const list = showingSearch ? searchResults : team;

  const selectedSet = new Set((value || []).map(String));

  const toggle = (rawId) => {
    const id = String(rawId);
    if (selectedSet.has(id)) {
      onChange(value.filter((v) => String(v) !== id));
    } else {
      onChange([...value, id]);
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <div className="space-y-1.5">
        <label className={LABEL_BASE}>Assignees</label>
        <div
          className={cx(
            "flex items-center gap-2 rounded-lg border px-3.5 py-2.5",
            disabled ? "bg-gray-50" : "bg-white"
          )}
        >
          <Search size={16} className="text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => setOpen(true)}
            placeholder="Search user by name or email"
            disabled={disabled}
            className="w-full border-0 p-0 text-[0.95rem] outline-none placeholder:text-gray-400"
          />
        </div>

        {/* Chips */}
        {value.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {value.map((id) => {
              const u = hydrated.find((x) => String(x.id) === String(id));
              const label = u?.name || u?.email || id;
              return (
                <span
                  key={id}
                  className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium border border-blue-100"
                >
                  {label}
                  <button
                    type="button"
                    className="rounded-full px-1 text-blue-600 hover:bg-blue-100"
                    onClick={() => toggle(id)}
                    aria-label="Remove"
                    title="Remove"
                  >
                    ✕
                  </button>
                </span>
              );
            })}
          </div>
        ) : null}
      </div>

      {open && (
        <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-xl border bg-white shadow-xl">
          {isError ? (
            <div className="px-3 py-3 text-sm text-red-600">Failed to load users.</div>
          ) : isFetching && showingSearch ? (
            <div className="px-3 py-3 text-sm text-gray-500">Loading…</div>
          ) : list.length === 0 ? (
            <div className="px-3 py-3 text-sm text-gray-500">
              {showingSearch ? "No results" : "No team members yet"}
            </div>
          ) : (
            <ul className="max-h-64 overflow-auto py-1">
              {list.map((u) => {
                const id = String(u.id || u._id || "");
                const checked = selectedSet.has(id);
                return (
                  <li key={id}>
                    <button
                      type="button"
                      className={cx(
                        "flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50",
                        checked ? "bg-blue-50" : ""
                      )}
                      onClick={() => toggle(id)}
                    >
                      <div className="grid h-7 w-7 place-items-center rounded-full bg-gray-100 text-[10px] font-semibold text-gray-700">
                        {initials(u.name || u.email || "?")}
                      </div>
                      <div className="min-w-0">
                        <div className="truncate text-gray-900">
                          {u.name || "(No name)"} {u.role ? `(${u.role})` : ""}
                        </div>
                        {u.email ? (
                          <div className="truncate text-xs text-gray-500">
                            {u.email}
                          </div>
                        ) : null}
                      </div>
                      <div className="ml-auto">
                        <input
                          type="checkbox"
                          readOnly
                          checked={checked}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600"
                        />
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/* =============================================================================
   TAG INPUT (chips)
============================================================================= */
function TagsInput({ value = [], onChange }) {
  const [buf, setBuf] = useState("");
  const add = () => {
    const s = buf.trim();
    if (!s) return;
    if (!value.includes(s)) onChange([...value, s]);
    setBuf("");
  };
  const remove = (t) => onChange(value.filter((x) => x !== t));
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2">
        {value.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium border border-blue-100"
          >
            {t}
            <button
              type="button"
              onClick={() => remove(t)}
              className="rounded-full px-1 text-blue-600 hover:bg-blue-100"
              aria-label={`Remove ${t}`}
              title="Remove"
            >
              ✕
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          value={buf}
          onChange={(e) => setBuf(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder="Add a tag and press Enter"
          className={INPUT_BASE + " flex-1"}
        />
        <button
          type="button"
          onClick={add}
          className="px-3.5 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 shadow-sm"
        >
          Add
        </button>
      </div>
    </div>
  );
}

/* =============================================================================
   MAIN COMPONENT
============================================================================= */
export default function AddWorkModal({
  open,
  onClose,
  onCreated,
  initialType,
  preselectedClientId = "",
  parentWorkId = "",
}) {
  const { pathname } = useLocation();
  const routeType = useMemo(() => deriveTypeFromPath(pathname), [pathname]);
  const [type, setType] = useState(initialType || routeType);

  // Core fields
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState(DEFAULTS[type].priority);

  // Client
  const [clientId, setClientId] = useState(preselectedClientId || "");
  const [selectedClientObj, setSelectedClientObj] = useState(null);

  // Users/tags
  const [assignees, setAssignees] = useState([]); // array of user ids
  const [tags, setTags] = useState([]);

  // Address (no geo)
  const [keepAddrSynced, setKeepAddrSynced] = useState(true);
  const [addrStreet, setAddrStreet] = useState("");
  const [addrCity, setAddrCity] = useState("");
  const [addrState, setAddrState] = useState("");
  const [addrPostal, setAddrPostal] = useState("");
  const [addrCountry, setAddrCountry] = useState("");
  const [addrRaw, setAddrRaw] = useState("");

  // Schedule
  const [startLocal, setStartLocal] = useState("");
  const [endLocal, setEndLocal] = useState("");
  const [allDay, setAllDay] = useState(false);
  const [timezone, setTimezone] = useState("");

  // Task/job/appt specifics
  const [checklistBuf, setChecklistBuf] = useState("");
  const [checklist, setChecklist] = useState([]);
  const [serviceType, setServiceType] = useState("");
  const [expectedAmount, setExpectedAmount] = useState("");
  const [onsiteRequired, setOnsiteRequired] = useState(true);
  const [apptConfirmed, setApptConfirmed] = useState(false);
  const [routeFromLabel, setRouteFromLabel] = useState("");
  const [routeMinutes, setRouteMinutes] = useState("");

  // Quote link (jobs)
  const [quoteId, setQuoteId] = useState("");
  const [selectedQuoteObj, setSelectedQuoteObj] = useState(null);

  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const formRef = useRef(null);

  // Reset defaults when type changes
  useEffect(() => {
    setPriority(DEFAULTS[type]?.priority || "normal");
    setFormError("");
    if (type !== "job") {
      setQuoteId("");
      setSelectedQuoteObj(null);
    }
  }, [type]);

  // sync address from client
  useEffect(() => {
    if (!keepAddrSynced) return;
    const a = selectedClientObj?.address || {};
    setAddrStreet(a.street || "");
    setAddrCity(a.city || "");
    setAddrState(a.state || "");
    setAddrPostal(a.zip || a.postalCode || "");
    setAddrCountry(a.country || "");
    setAddrRaw(
      a.raw ||
        [a.street, a.city, a.state, a.zip || a.postalCode, a.country]
          .filter(Boolean)
          .join(", ")
    );
  }, [selectedClientObj, keepAddrSynced]);

  // If user links a quote and no client picked yet → auto-pick quote.client
  useEffect(() => {
    if (!selectedQuoteObj || type !== "job" || clientId) return;

    const resolvedId = (() => {
      const direct =
        selectedQuoteObj.clientId ||
        selectedQuoteObj.clientID ||
        selectedQuoteObj.client_id;
      if (direct) return String(direct);
      const c = selectedQuoteObj.client;
      if (!c) return "";
      if (typeof c === "string") return String(c);
      return String(c._id || c.id || "");
    })();

    if (!resolvedId) return;

    setClientId(resolvedId);

    if (selectedQuoteObj.client && typeof selectedQuoteObj.client === "object") {
      setSelectedClientObj(selectedQuoteObj.client);
    } else if (keepAddrSynced) {
      (async () => {
        try {
          const res = await api.get(`/clients/${resolvedId}`);
          const c = res?.data?.data || res?.data || null;
          if (c) setSelectedClientObj(c);
        } catch {
          /* noop */
        }
      })();
    }
  }, [selectedQuoteObj, type, clientId, keepAddrSynced]);

  const buildPayload = useCallback(() => {
    if (!title.trim()) throw new Error("Title is required.");
    if (type === "job" && !clientId) throw new Error("Client is required for jobs.");
    if (type === "appointment" && (!startLocal || (!clientId && !parentWorkId))) {
      throw new Error(
        "Appointment requires a start time and either a client or parent work."
      );
    }
    if (startLocal && endLocal && new Date(endLocal) < new Date(startLocal)) {
      throw new Error("End time must be after start time.");
    }
    if (type === "job" && selectedQuoteObj && clientId) {
      const qcid =
        selectedQuoteObj.clientId ||
        selectedQuoteObj.clientID ||
        selectedQuoteObj.client_id ||
        (typeof selectedQuoteObj.client === "string"
          ? selectedQuoteObj.client
          : selectedQuoteObj.client?._id || selectedQuoteObj.client?.id);
      if (qcid && String(qcid) !== String(clientId)) {
        throw new Error(
          "Selected quote belongs to a different client. Please change the client or remove the quote."
        );
      }
    }

    const payload = {
      type,
      title: title.trim(),
      description: description.trim() || undefined,
      priority,
      clientId: clientId || undefined,
      parentWorkId: parentWorkId || undefined,
      assignees: Array.from(new Set((assignees || []).map(String))).filter(Boolean),
      tags,
    };

    if (type === "job" && quoteId) payload.quoteId = quoteId;

    const hasAddress =
      addrStreet || addrCity || addrState || addrPostal || addrCountry || addrRaw;
    if (hasAddress) {
      payload.address = {
        street: addrStreet || undefined,
        city: addrCity || undefined,
        state: addrState || undefined,
        postalCode: addrPostal || undefined,
        country: addrCountry || undefined,
        raw: addrRaw || undefined,
      };
    }

    const sch = {};
    const sISO = toISO(startLocal);
    const eISO = toISO(endLocal);
    if (sISO) sch.start = sISO;
    if (eISO) sch.end = eISO;
    if (allDay) sch.allDay = true;
    // only send timezone if explicitly set; otherwise backend uses company default
    if (timezone && timezone.trim()) {
      sch.timezone = timezone.trim();
    }
    if (Object.keys(sch).length) payload.schedule = sch;

    if (type === "task") {
      if (checklist.length) {
        payload.taskProfile = {
          checklist: checklist.map((c) => ({ label: c.label, done: !!c.done })),
        };
      }
    } else if (type === "job") {
      const amt = expectedAmount
        ? Number(String(expectedAmount).replace(/[^0-9.-]/g, ""))
        : undefined;
      payload.jobProfile = {
        serviceType: serviceType || undefined,
        onsiteRequired: !!onsiteRequired,
        pricing: Number.isFinite(amt) ? { expectedAmount: amt } : undefined,
      };
    } else if (type === "appointment") {
      payload.appointmentProfile = {
        confirmed: !!apptConfirmed,
        route: {
          fromLabel: routeFromLabel || undefined,
          travelMinutes: routeMinutes ? Number(routeMinutes) : undefined,
        },
      };
    }

    return payload;
  }, [
    title,
    description,
    priority,
    clientId,
    parentWorkId,
    assignees,
    tags,
    addrStreet,
    addrCity,
    addrState,
    addrPostal,
    addrCountry,
    addrRaw,
    startLocal,
    endLocal,
    allDay,
    timezone,
    type,
    checklist,
    serviceType,
    expectedAmount,
    onsiteRequired,
    apptConfirmed,
    routeFromLabel,
    routeMinutes,
    quoteId,
    selectedQuoteObj,
  ]);

  const { mutateAsync: createWork } = useCreateWork();

  const submit = async (e) => {
    e.preventDefault();
    setFormError("");
    try {
      setSaving(true);
      const payload = buildPayload();
      const created = await createWork(payload);
      toast.success("Work created");
      onCreated?.(created);
      onClose?.();
    } catch (err) {
      const msg =
        err?.message || err?.response?.data?.message || "Failed to create work";
      setFormError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // checklist helpers
  const addChecklist = () => {
    const label = checklistBuf.trim();
    if (!label) return;
    setChecklist((prev) => [
      ...prev,
      { id: crypto.randomUUID?.() || String(Date.now()), label, done: false },
    ]);
    setChecklistBuf("");
  };
  const removeChecklist = (id) =>
    setChecklist((prev) => prev.filter((c) => c.id !== id));

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="relative bg-white w-full max-w-6xl rounded-2xl shadow-2xl flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-t-2xl">
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center shrink-0">
              <Building2 size={32} />
            </div>
            <div className="min-w-0">
              <h2 className="text-xl font-semibold truncate">Create Work</h2>
              <p className="text-sm text-blue-100">
                Add a task, job, or appointment.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="bg-white/20 hover:bg-white/30 rounded-full p-2"
            type="button"
            aria-label="Close"
            disabled={saving}
          >
            <X />
          </button>
        </div>

        {/* Form */}
        <form
          ref={formRef}
          onSubmit={submit}
          className="flex-1 overflow-y-auto p-8 space-y-8 bg-gray-50/50"
        >
          <div className="space-y-6">
            {/* Basic */}
            <Section
              icon={Info}
              title="Basic Information"
              hint="Core details for this work item (status will be set automatically)."
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <LabeledSelect
                  label="Type"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                >
                  {TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t[0].toUpperCase() + t.slice(1)}
                    </option>
                  ))}
                </LabeledSelect>

                <LabeledSelect
                  label="Priority"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                >
                  {PRIORITIES.map((p) => (
                    <option key={p} value={p}>
                      {p[0].toUpperCase() + p.slice(1)}
                    </option>
                  ))}
                </LabeledSelect>

                <LabeledInput
                  label="Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Install new HVAC unit"
                  required
                  autoComplete="off"
                />
                <div className="md:col-span-3">
                  <label className={LABEL_BASE}>Description</label>
                  <textarea
                    rows={2}
                    className={INPUT_BASE + " h-auto"}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Optional details…"
                  />
                </div>
              </div>
            </Section>

            {/* Client + Assignees */}
            <Section
              icon={Users}
              title="Client & Assignment"
              hint="Search a client and choose team members"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ClientCombobox
                  selectedClientId={clientId}
                  onSelectClientId={setClientId}
                  onSelectClientObject={setSelectedClientObj}
                  disabled={saving}
                />
                <UsersMultiSelect
                  value={assignees}
                  onChange={setAssignees}
                  disabled={saving}
                />
              </div>
            </Section>

            {/* Schedule */}
            <Section
              icon={CalendarDays}
              title={
                type === "appointment"
                  ? "Schedule (required for appointments)"
                  : "Schedule (optional)"
              }
              hint="Set the time window and timezone"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <LabeledInput
                  label={
                    <>
                      Start{" "}
                      {type === "appointment" ? (
                        <span className="text-red-500">*</span>
                      ) : null}
                    </>
                  }
                  type="datetime-local"
                  value={startLocal}
                  onChange={(e) => setStartLocal(e.target.value)}
                  required={type === "appointment"}
                  rightAdornment={<Clock size={16} />}
                />
                <LabeledInput
                  label="End"
                  type="datetime-local"
                  value={endLocal}
                  onChange={(e) => setEndLocal(e.target.value)}
                  rightAdornment={<Clock size={16} />}
                />

                <div className="flex items-center gap-3">
                  <input
                    id="allDay"
                    type="checkbox"
                    checked={allDay}
                    onChange={(e) => setAllDay(e.target.checked)}
                  />
                  <label htmlFor="allDay" className="text-[13px] text-gray-700">
                    All day
                  </label>
                </div>

                <LabeledInput
                  label="Timezone"
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  placeholder="Leave empty to use company default (e.g., America/Toronto)"
                />
              </div>
              {type === "appointment" && !startLocal ? (
                <div className="mt-2 text-[12px] text-red-600">
                  Appointment requires a start date/time.
                </div>
              ) : null}
              {startLocal && endLocal && new Date(endLocal) < new Date(startLocal) ? (
                <div className="mt-2 text-[12px] text-red-600">
                  End must be after start.
                </div>
              ) : null}
            </Section>

            {/* Address */}
            <Section
              icon={MapPin}
              title="Address"
              hint="Prefill from the selected client or edit as needed"
            >
              <div className="flex items-center gap-3 mb-2">
                <input
                  id="syncAddr"
                  type="checkbox"
                  checked={keepAddrSynced}
                  onChange={(e) => setKeepAddrSynced(e.target.checked)}
                />
                <label htmlFor="syncAddr" className="text-[13px] text-gray-700">
                  Keep address synced with client
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <LabeledInput
                  label="Street"
                  value={addrStreet}
                  onChange={(e) => {
                    setKeepAddrSynced(false);
                    setAddrStreet(e.target.value);
                  }}
                  disabled={keepAddrSynced}
                />
                <LabeledInput
                  label="City"
                  value={addrCity}
                  onChange={(e) => {
                    setKeepAddrSynced(false);
                    setAddrCity(e.target.value);
                  }}
                  disabled={keepAddrSynced}
                />
                <LabeledInput
                  label="State/Province"
                  value={addrState}
                  onChange={(e) => {
                    setKeepAddrSynced(false);
                    setAddrState(e.target.value);
                  }}
                  disabled={keepAddrSynced}
                />
                <LabeledInput
                  label="ZIP/Postal"
                  value={addrPostal}
                  onChange={(e) => {
                    setKeepAddrSynced(false);
                    setAddrPostal(e.target.value);
                  }}
                  disabled={keepAddrSynced}
                />
                <LabeledInput
                  label="Country"
                  value={addrCountry}
                  onChange={(e) => {
                    setKeepAddrSynced(false);
                    setAddrCountry(e.target.value);
                  }}
                  disabled={keepAddrSynced}
                />
                <LabeledInput
                  label="Raw (optional)"
                  value={addrRaw}
                  onChange={(e) => {
                    setKeepAddrSynced(false);
                    setAddrRaw(e.target.value);
                  }}
                  disabled={keepAddrSynced}
                />
              </div>
            </Section>

            {/* Type-specific */}
            {type === "task" ? (
              <Section
                icon={UserIcon}
                title="Task Checklist"
                hint="Track steps to complete the task"
              >
                <div className="flex items-center gap-2">
                  <input
                    value={checklistBuf}
                    onChange={(e) => setChecklistBuf(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addChecklist();
                      }
                    }}
                    placeholder="Add checklist item"
                    className={INPUT_BASE + " flex-1"}
                  />
                  <button
                    type="button"
                    onClick={addChecklist}
                    className="px-3.5 py-2.5 rounded-lg border text-sm hover:bg-slate-50"
                  >
                    <Plus size={16} className="inline -mt-1 mr-1" />
                    Add
                  </button>
                </div>
                {checklist.length > 0 ? (
                  <ul className="mt-3 space-y-2">
                    {checklist.map((c) => (
                      <li
                        key={c.id}
                        className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                      >
                        <span>{c.label}</span>
                        <button
                          type="button"
                          onClick={() => removeChecklist(c.id)}
                          className="rounded-md p-1 text-gray-600 hover:bg-gray-100"
                        >
                          <X size={16} />
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </Section>
            ) : null}

            {type === "job" ? (
              <>
                <Section
                  icon={Building2}
                  title="Job Details"
                  hint="Service and pricing information"
                >
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <LabeledInput
                      label="Service Type"
                      value={serviceType}
                      onChange={(e) => setServiceType(e.target.value)}
                      placeholder="e.g., Installation"
                    />
                    <LabeledInput
                      label="Expected Amount"
                      value={expectedAmount}
                      onChange={(e) => setExpectedAmount(e.target.value)}
                      placeholder="e.g., 1200"
                      type="text"
                    />
                    <div className="flex items-center gap-3 pt-7">
                      <input
                        id="onsiteReq"
                        type="checkbox"
                        checked={onsiteRequired}
                        onChange={(e) => setOnsiteRequired(e.target.checked)}
                      />
                      <label htmlFor="onsiteReq" className="text-[13px] text-gray-700">
                        Onsite required
                      </label>
                    </div>
                  </div>
                </Section>

                <Section
                  icon={FileText}
                  title="Link Quote (optional)"
                  hint="Linking a quote sets lineage. Client must match the quote's client."
                >
                  <QuoteCombobox
                    selectedQuoteId={quoteId}
                    onSelectQuoteId={setQuoteId}
                    onSelectQuoteObject={setSelectedQuoteObj}
                    disabled={saving}
                  />
                </Section>
              </>
            ) : null}

            {type === "appointment" ? (
              <Section
                icon={CalendarDays}
                title="Appointment Details"
                hint="Confirmation and travel"
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center gap-3">
                    <input
                      id="apptConfirmed"
                      type="checkbox"
                      checked={apptConfirmed}
                      onChange={(e) => setApptConfirmed(e.target.checked)}
                    />
                    <label htmlFor="apptConfirmed" className="text-[13px] text-gray-700">
                      Confirmed
                    </label>
                  </div>
                  <LabeledInput
                    label="Route From (label)"
                    value={routeFromLabel}
                    onChange={(e) => setRouteFromLabel(e.target.value)}
                    placeholder="e.g., Warehouse"
                  />
                  <LabeledInput
                    label="Travel Minutes"
                    value={routeMinutes}
                    onChange={(e) => setRouteMinutes(e.target.value)}
                    type="number"
                    placeholder="e.g., 35"
                  />
                </div>
              </Section>
            ) : null}

            {/* Tags */}
            <Section icon={TagIcon} title="Tags" hint="Organize and filter work">
              <TagsInput value={tags} onChange={setTags} />
            </Section>

            {/* Guidance / Errors */}
            {formError ? (
              <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {formError}
              </div>
            ) : (
              <div className={HINT_BASE + " flex items-center gap-2"}>
                <Info size={14} className="text-gray-400" />
                Jobs require a client. Appointments require a start time and either a
                client or a parent work item.
              </div>
            )}
          </div>
        </form>

        {/* Sticky Footer */}
        <div className="px-8 py-4 bg-white border-t flex justify-end gap-3 shadow-lg rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 rounded-lg text-gray-700 font-medium hover:bg-gray-100 border border-gray-200"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => formRef.current?.requestSubmit()}
            className={cx(
              "px-7 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 shadow-sm",
              saving ? "opacity-60 cursor-not-allowed" : ""
            )}
            disabled={saving}
          >
            {saving ? "Creating…" : "Create Work"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
